import { useCallback, useEffect, useRef, useState } from 'react';

import { QueryHookOptions, useApolloClient } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  ILoyaltyUserOfferItemsQuery,
  LoyaltyOfferRedemptionType,
  LoyaltyPaymentMethod,
  LoyaltyPromotionType,
  LoyaltyServiceMode,
  LoyaltyUserOfferItemsDocument,
  useLoyaltyOfferItemsLazyQuery,
  useLoyaltyUserOfferItemsQuery,
} from 'generated/graphql-gateway';
import { useLoyaltySystemwideOffersByIdsQuery } from 'generated/sanity-graphql';
import { useLocaleSmartBlockContent } from 'hooks/use-locale-smart-block-content/use-locale-smart-block-content';
import { groqQuery } from 'remote/api/groq';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { parseOffers } from 'state/global-state/models/loyalty/offers/offers.utils';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useNetworkContext } from 'state/network';
import { useServiceModeContext } from 'state/service-mode';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

import {
  AppPersonalizedOffer,
  AppPersonalizedOfferGroup,
  EngineNonPersonalizedOfferListItem,
  EngineOfferListItem,
  EnginePersonalizedOffer,
  LoyaltyAppliedOffer,
  LoyaltyOffer,
  SanityOfferListItemWithExtras,
  isEngineNonPersonalizedOfferListItem,
  isEnginePersonalizedOffer,
  isEnginePersonalizedOfferGroup,
} from '../types';
import { upsertSanityOfferLists } from '../utils';

import { SORTED_LOYALTY_OFFERS_GROQ } from './sortedLoyaltyOffersQuery';
import { IAppliedRewards, IEntriesIdsMap, IQueryLoyaltyUserOffersOptions } from './types';
import { useLoyaltyIncentivesEvaluation } from './use-loyalty-incentive-evaluation';
import { useLoyaltyUserId } from './use-loyalty-user';
import { usePersonalizedOffers } from './use-personalized-offers';
import { flattenEntriesToMap, parseEntry } from './utils/incentive-evaluation';
import { processPersonalizedOfferGroup } from './utils/personalized-offer-groups';
import { IPersonalizedData, mergePersonalizedData } from './utils/personalized-offers';

/** @deprecated useLoyaltyContext instead as this hook has side effects and should only be mounted once */
export const useLoyaltyOffers = () => {
  // This flag is parent to the next ones
  const loyaltyOffersEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_OFFERS));
  // This flag replaces Offers 3.0
  const loyaltyStandardOffersEnabled = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS)
  );

  const enableLoyaltyStandardOffers = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_STANDARD_OFFERS);
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));
  const client = useApolloClient();
  const [evaluating, setEvaluating] = useState(false);
  const dispatch = useAppDispatch();
  const { loyaltyUserId } = useLoyaltyUserId();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const engineSystemwideOffers = useAppSelector(selectors.loyalty.selectEngineOfferListItems);
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const sortedOffersRef = useRef<(LoyaltyOffer | AppPersonalizedOffer)[]>([]);
  const hasFetchedCmsOffersRef = useRef(false);
  const isFetchingCmsOffersRef = useRef(false);
  const cartEntriesIdsMap = useRef<IEntriesIdsMap>({});
  const { language } = useLocale();

  const loyaltyServiceMode =
    Object.entries(LoyaltyServiceMode).find(([key]) => key === serviceMode)?.[1] || undefined;

  const [
    fetchPersonalizedOfferData,
    { loading: personalizedDataLoading },
  ] = usePersonalizedOffers();
  const storeId = store?.number;

  const { transformSmartBlockContent } = useLocaleSmartBlockContent();
  const { sanityEndpoints } = useNetworkContext();
  const [cmsOffersLoading, setCmsOffersLoading] = useState(false);

  const queryFeatureSortedLoyaltyOffers = useCallback(async () => {
    if (isFetchingCmsOffersRef.current) {
      return;
    }
    isFetchingCmsOffersRef.current = true;
    setCmsOffersLoading(true);
    try {
      const data = await groqQuery<
        { sortedSystemwideOffers: SanityOfferListItemWithExtras[] | null },
        { language: string }
      >(
        sanityEndpoints.groq,
        SORTED_LOYALTY_OFFERS_GROQ,
        { language },
        { loggingTag: 'sortedSystemwideOffers' }
      );

      if (data?.sortedSystemwideOffers) {
        sortedOffersRef.current = upsertSanityOfferLists(
          sortedOffersRef.current,
          data.sortedSystemwideOffers as LoyaltyOffer[]
        );
      }
      hasFetchedCmsOffersRef.current = true;
    } catch (error) {
      logger.error({ message: 'groq fetch sortedSystemwideOffers error', error });
    } finally {
      isFetchingCmsOffersRef.current = false;
      setCmsOffersLoading(false);
    }
  }, [sanityEndpoints.groq, language]);

  const refetchCmsOffers = queryFeatureSortedLoyaltyOffers;

  const getPersonalizedOffer = useCallback(
    (engineOffer: EnginePersonalizedOffer, personalizedOfferData: IPersonalizedData) => {
      let personalizedOffer: AppPersonalizedOffer | null = null;

      try {
        personalizedOffer = mergePersonalizedData({
          engineOffer,
          transformSmartBlockContent,
          ...personalizedOfferData,
        });
      } catch (error) {
        const err = toError(error);
        const message = err.message ? `Error: ${err.message}` : '';
        logger.error(`Failed processing personalized offer: ${engineOffer.id} - ${message}`);
      }

      return personalizedOffer;
    },
    [transformSmartBlockContent]
  );

  const onUserOffersQueryComplete = useCallback(
    async (data: ILoyaltyUserOfferItemsQuery) => {
      const loyaltyUser = data?.loyaltyUserV2;
      const offerRedemptionAvailableAfter =
        loyaltyUser?.offerRedemptionAvailability?.availableAfter;

      if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
        dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
      }

      const engineUserOfferItems = (loyaltyUser?.offerItems || []) as EngineOfferListItem[];
      if (!engineUserOfferItems.length) {
        return;
      }

      // Fetch data needed to build personalized offer
      const personalizedOfferData = await fetchPersonalizedOfferData(engineUserOfferItems);

      const appPersonalizedOffers: AppPersonalizedOffer[] = [];
      const appPersonalizedOfferGroups: AppPersonalizedOfferGroup[] = [];
      const engineOfferListItems: EngineNonPersonalizedOfferListItem[] = [];

      engineUserOfferItems.forEach(item => {
        if (isEnginePersonalizedOffer(item) && personalizedOfferData) {
          const personalizedOffer = getPersonalizedOffer(item, personalizedOfferData);
          if (personalizedOffer) {
            appPersonalizedOffers.push(personalizedOffer);
          }
        }

        if (isEnginePersonalizedOfferGroup(item) && personalizedOfferData) {
          const group = processPersonalizedOfferGroup(
            item,
            personalizedOfferData.systemwideOffersMap,
            personalizedOfferData.offerGroupsMap
          );
          if (group) {
            appPersonalizedOfferGroups.push(group);
          }
        }

        if (isEngineNonPersonalizedOfferListItem(item)) {
          engineOfferListItems.push(item);
        }
      });

      sortedOffersRef.current = sortedOffersRef.current.concat(appPersonalizedOffers);

      dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
      dispatch(actions.loyalty.setEngineOfferListItems(engineOfferListItems));
      dispatch(actions.loyalty.setAppPersonalizedOffers(appPersonalizedOffers));
      dispatch(actions.loyalty.setAppPersonalizedOfferGroups(appPersonalizedOfferGroups));
    },
    [dispatch, fetchPersonalizedOfferData, getPersonalizedOffer, offersCooldownEnabled]
  );

  const [queryEngineOffers, { loading: engineOffersLoading }] = useLoyaltyOfferItemsLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      where: {
        serviceMode: loyaltyServiceMode,
        storeId,
        omitInvalids: false,
        includeOfferGroups: true,
      },
    },
    onCompleted: data => {
      if (data?.loyaltyOfferItems?.length) {
        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));
        dispatch(
          actions.loyalty.setGuestEngineOfferListItems(
            data.loyaltyOfferItems as EngineOfferListItem[]
          )
        );
      }
    },
  });

  // Attach applied offers ids to get the full collection of offers from CMS
  const appliedLoyaltyOffersIds = appliedOffers.map(({ cmsId }) => cmsId || '');

  const { loading: queryUserAppliedOffersLoading } = useLoyaltyUserOfferItemsQuery({
    fetchPolicy: 'no-cache',
    skip: !loyaltyUserId || !appliedLoyaltyOffersIds.length || engineSystemwideOffers.length > 0,
    variables: {
      loyaltyId: loyaltyUserId,
      where: {
        ids: appliedOffers.map(({ id }) => id || ''),
        includeOfferGroups: true,
      },
    },
    onCompleted: onUserOffersQueryComplete,
  });

  const { loading: swoffersLoading } = useLoyaltySystemwideOffersByIdsQuery({
    variables: {
      ids: appliedLoyaltyOffersIds,
    },
    skip: !appliedLoyaltyOffersIds.length,
    onCompleted(data) {
      if (data?.allSystemwideOffers) {
        sortedOffersRef.current = sortedOffersRef.current.concat(
          data.allSystemwideOffers as LoyaltyOffer[]
        );

        dispatch(actions.loyalty.setCmsOffers(sortedOffersRef.current));

        const upsizeAndDiscountIds = appliedOffers
          .filter(({ cartId }) => cartId === DISCOUNT_OFFER_CART_ID)
          .map(({ id }) => id || '');

        if (upsizeAndDiscountIds.length) {
          queryUserOffers({
            variables: {
              loyaltyId: loyaltyUserId,
              where: {
                omitInvalids: false,
                ids: upsizeAndDiscountIds,
              },
            },
          });
        }
      }
    },
  });

  const [queryUserOffersLoading, setQueryUserOffersLoading] = useState(false);
  const queryUserOffers = useCallback(
    async ({ variables, ...options }: QueryHookOptions<any, any>) => {
      const { where, ...otherVariables } = variables || {};
      setQueryUserOffersLoading(true);
      dispatch(actions.loyalty.setOffersLoading(true));

      try {
        const { data } = await client.query<ILoyaltyUserOfferItemsQuery>({
          ...options,
          fetchPolicy: 'no-cache',
          query: LoyaltyUserOfferItemsDocument,
          variables: {
            ...otherVariables,
            where: {
              ...where,
              includeOfferGroups: true,
            },
          },
        });

        if (data) {
          onUserOffersQueryComplete(data);
        }
      } catch (e) {
        logger.error(`Error fetching user offers: ${e}`);
      } finally {
        setQueryUserOffersLoading(false);
        dispatch(actions.loyalty.setOffersLoading(false));
      }
    },
    [client, dispatch, onUserOffersQueryComplete]
  );

  const queryLoyaltyUserOffers = useCallback(
    ({
      redemptionTypes,
      omitInvalids = false,
    }: {
      redemptionTypes: LoyaltyOfferRedemptionType[];
      omitInvalids?: boolean;
    }) => ({
      loyaltyId,
      cartEntries,
      appliedRewards,
      appliedLoyaltyOffers = [],
    }: IQueryLoyaltyUserOffersOptions) => {
      const parsedEntries = cartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.OFFER,
          incentivesToExclude: appliedRewards,
        }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      queryUserOffers({
        variables: {
          loyaltyId,
          where: {
            appliedIncentives: parseOffers(appliedLoyaltyOffers),
            cartEntries: parsedEntries,
            serviceMode: serviceMode || undefined,
            redemptionTypes,
            storeId,
            omitInvalids,
          },
        },
      });
    },
    [queryUserOffers, serviceMode, storeId]
  );

  // Offers without loyaltyId should always be of redemption type STANDARD
  const queryLoyaltyOffers = useCallback(() => {
    queryEngineOffers({
      variables: {
        where: {
          serviceMode: loyaltyServiceMode,
          storeId,
          redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD],
          includeOfferGroups: true,
        },
      },
    });
  }, [queryEngineOffers, loyaltyServiceMode, storeId]);

  /**
   * Lazy-load sortedSystemwideOffers (CMS only). Call when a screen that needs offers mounts
   * (e.g. Loyalty Offers, Cart). Idempotent after first successful fetch — does not re-trigger
   * engine offer queries; screens run their own fetch flow (e.g. Offers uses refreshOffers/fetchOffers).
   */
  const ensureSortedOffersLoaded = useCallback(async () => {
    const shouldSkipLoad =
      !enableLoyaltyStandardOffers ||
      hasFetchedCmsOffersRef.current ||
      isFetchingCmsOffersRef.current;
    if (shouldSkipLoad) {
      return;
    }
    await queryFeatureSortedLoyaltyOffers();
    queryLoyaltyOffers();
  }, [enableLoyaltyStandardOffers, queryFeatureSortedLoyaltyOffers, queryLoyaltyOffers]);

  const queryLoyaltyUserStandardOffers = useCallback(
    (options: IQueryLoyaltyUserOffersOptions) =>
      queryLoyaltyUserOffers({
        redemptionTypes: [LoyaltyOfferRedemptionType.STANDARD],
      })(options),
    [queryLoyaltyUserOffers]
  );

  const { evaluateLoyaltyIncentives } = useLoyaltyIncentivesEvaluation();

  /**
   * Evaluates applied loyalty offers against cart entries.
   *
   * IMPORTANT: Callers must ensure the unified menu is loaded before calling
   * this function. Use `indexedMenu.isLoaded()` from `useUnifiedMenuContext()`
   * to check. This avoids stale closure issues when the check is done inside
   * this callback.
   *
   * @see YourCart - guards with `isUnifiedMenuLoaded` in useEffect
   * @see useCartAvailabilityCheck - guards with `isUnifiedMenuLoaded` in
   * useEffect
   */
  const evaluateLoyaltyUserOffers = useCallback(
    async (
      loyaltyId: string,
      appliedLoyaltyOffers: LoyaltyAppliedOffer[],
      cartEntries?: ILegacyCartEntry[],
      appliedLoyaltyRewards?: IAppliedRewards | null,
      subtotalAmount?: number,
      paymentMethod?: LoyaltyPaymentMethod | null
    ) => {
      const parsedEntries = cartEntries?.reduce(
        parseEntry({
          type: LoyaltyPromotionType.OFFER,
          incentivesToExclude: appliedLoyaltyRewards,
        }),
        []
      );

      cartEntriesIdsMap.current = (parsedEntries || []).reduce(flattenEntriesToMap, {});

      setEvaluating(true);

      try {
        const evaluationResult = await evaluateLoyaltyIncentives({
          loyaltyId,
          appliedIncentives: appliedLoyaltyOffers,
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries: parsedEntries,
          subtotalAmount,
          paymentMethod,
          serviceMode,
          storeId,
        });

        if (evaluationResult) {
          dispatch(actions.loyalty.setOffersFeedbackMap(evaluationResult));
        }
      } finally {
        setEvaluating(false);
      }
    },
    [dispatch, evaluateLoyaltyIncentives, serviceMode, storeId]
  );

  useEffect(() => {
    dispatch(
      actions.loyalty.setOffersLoading(
        queryUserOffersLoading ||
          engineOffersLoading ||
          personalizedDataLoading ||
          cmsOffersLoading ||
          swoffersLoading ||
          evaluating ||
          queryUserAppliedOffersLoading
      )
    );
  }, [
    dispatch,
    queryUserOffersLoading,
    engineOffersLoading,
    personalizedDataLoading,
    cmsOffersLoading,
    swoffersLoading,
    evaluating,
    queryUserAppliedOffersLoading,
  ]);

  return {
    evaluateLoyaltyUserOffers,
    ensureSortedOffersLoaded,
    loyaltyOffersEnabled,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    refetchCmsOffers,
    cmsOffersLoading,
  };
};
